/**
 * The site's one Google map component: a `@react-google-maps/api` map with a marker per
 * `mapData` item, an optional info window on the selected marker, and viewport callbacks.
 *
 * Position: used by `components/Locations/LocationsOnMap.tsx` (the locations finder, the only
 * caller of the selection, hover and viewport callbacks),
 * `components/Locations/IndividualLocation.tsx` (one pin per location page),
 * `components/Shared/YourMetalExperts/ContactUsPlant.tsx`,
 * `components/NewsEvents/Events/EventDetails.tsx` and
 * `components/FamilyOfCompanies/FamilyOfCompaniesRefactor/Company.tsx`. Every caller passes
 * `NEXT_PUBLIC_GOOGLE_MAPS_KEY` as `googleKey`.
 *
 * Rules a change must keep:
 * - It renders nothing until the Maps script has loaded, `googleKey` is non-empty and the
 *   component has mounted in the browser. With no key there is no map, and the finder's list
 *   holds no Contentful locations while it is in its viewport mode (that list is filled from
 *   {@link MapProps.updateBoundaryFilter}). Applying a search point or saving multiple locations
 *   switches the finder to its distance list, which fills without a map
 *   (`components/Locations/LocationsOnMap.tsx` → `refreshMapLocations`).
 * - It loads the `places` library with the map. `components/Locations/LocationInput.tsx` uses
 *   that library and doesn't load it, so the address search works only on a page that renders a
 *   map. This is the only `useJsApiLoader` in the repo. The script loads once per page, and the
 *   loader throws ("Loader must not be called again with different options") if a second caller
 *   asks for a different key or `libraries`.
 * - The first idle always sets the zoom to `zoomLevel`. Without `center`, the view is first
 *   fitted to the markers' bounds, so the map ends centred on them at `zoomLevel`; with
 *   `center`, it starts there and follows later `center` changes.
 *
 * As the code stands: `customMarkerScale` doesn't reach the markers (the computed `size` is only
 * used as a render guard), so marker images show at their file size; a marker's `title` (the
 * hover tooltip) is `"undefined"` unless the item has a `title`, which `MapData` doesn't declare;
 * and an empty `mapData` renders a `0` inside the map.
 */
import React, { useState, useEffect, useRef } from "react";
import { GoogleMap, Marker, InfoWindow, useJsApiLoader } from "@react-google-maps/api";
import { v4 as uuidv4 } from "uuid";
/* 
 @External Dependancy Needed : @react-google-maps/api
  @Info: https://www.npmjs.com/package/@react-google-maps/api
  @Installed: yarn add @react-google-maps/api
*/

/**
 * A marker position in decimal degrees. `customMarker` and `iconLabel` here are not read: the
 * marker reads them from the `mapData` item itself.
 */
export interface MapLocation {
	lat: number;
	lng: number;
	customMarker?: string;
	iconLabel?: any;
}

/**
 * One marker. `id` must be unique among the items: the info window opens on the item whose `id`
 * equals the selected one. `name` is the React key. Callers may add `customMarker`, `iconLabel`
 * and `title` to the item; the marker reads them although this type doesn't declare them.
 */
export interface MapData {
	location: MapLocation;
	name: string;
	id: string | number;
}

/** Props of {@link Map}. */
interface MapProps {
	googleKey: string;
	mapData: Array<MapData>;
	dataTestId?: string;
	id?: string;
	className?: string;
	/* @desc- will  inherit if not passsed in*/
	width?: number;
	height?: number;
	/** A `mapData` item whose info window opens; follows the prop when it changes. */
	markerToSelect?: {};
	/** Google zoom level set on the first idle; see the file header. */
	zoomLevel?: number;
	/* @desc- imported image or url of file*/
	customMarker?: string;
	/** `{ lat, lng }`. Absent: the view is fitted to the markers. */
	center?: any;
	/** Google marker label for every marker that has no `iconLabel` of its own. */
	iconLabel?: any;
	/** Not applied to the markers; see the file header. */
	customMarkerScale?: [number, number]; //[width,height]
	handleInfoWindowClosed?: (item: any) => void;
	/** Called with the clicked item. Markers are clickable only when this is passed. */
	handleMarkerSelected?: (mapData: any) => void;
	/** Called once with the Google `Map` instance when it loads. */
	handMapLoaded?: (map?: any) => void;
	handleMapUnmount?: () => void;
	handleMarkerHover?: (event?: any, data?: any) => void;
	/* @desc- Function that wraps/returns JSX Element to display info pop up*/
	infoWindowContent?: (props: any) => JSX.Element;
	/**
	 * Called with the visible bounds on every idle and at the end of every drag. Despite the
	 * names, the four values are, in order: north latitude, west longitude, south latitude, east
	 * longitude. The finder (`components/Locations/LocationsOnMap.tsx`) builds its list from them.
	 */
	updateBoundaryFilter?: (
		topLeftX: any,
		topLeftY: any,
		bottomRightX: any,
		bottomRightY: any
	) => void;
}

/**
 * The map; see the file header. Size is `width` / `height` in pixels, or the container's size
 * when absent. Without an `id` prop, a new random `id` is generated on every render. Each marker's
 * icon is the item's `customMarker`, else the `customMarker` prop, else Google's default pin.
 */
const Map: React.FC<MapProps> = ({
	googleKey,
	mapData,
	width,
	height,
	markerToSelect,
	zoomLevel,
	handleInfoWindowClosed,
	handleMarkerSelected,
	handleMarkerHover,
	handMapLoaded,
	handleMapUnmount,
	infoWindowContent,
	customMarker,
	center,
	iconLabel,
	customMarkerScale = [25, 25],
	className = "",
	dataTestId,
	id = uuidv4(),
	updateBoundaryFilter,
}) => {
	const { isLoaded } = useJsApiLoader({
		googleMapsApiKey: googleKey,
		libraries: ["places"],
	});
	const [hasMounted, setHasMounted] = useState<boolean>(false);
	const [map, setMap] = React.useState<any>(null);
	const [hasIdled, setHasIdled] = React.useState<boolean>(false);
	const [centerPosition, setCenterPosition] = useState(center ?? null);
	const [selectedMarkers, setSelectedMarkers] = useState<any>(
		(markerToSelect && markerToSelect) || {}
	);

	const onLoad = React.useCallback((map: any) => {
		setMap(map);
		handMapLoaded && handMapLoaded(map);
	}, []);

	const handleDragEndEvent = () => {
		if (!map) return;
		if (updateBoundaryFilter) {
			updateBoundaryFilter(
				map.getBounds().getNorthEast().lat(),
				map.getBounds().getSouthWest().lng(),
				map.getBounds().getSouthWest().lat(),
				map.getBounds().getNorthEast().lng()
			);
		}
		const newCenter = map.getCenter().toJSON();
		setCenterPosition(newCenter);
	};

	const onUnmount = React.useCallback(() => {
		handleMapUnmount && handleMapUnmount();
		setMap(null);
	}, []);

	const handleMarkerClicked = (e: any, item: any) => {
		handleMarkerSelected && handleMarkerSelected(item);
		setSelectedMarkers(item);
	};

	const handleCloseInfoWindow = (item: any) => {
		handleInfoWindowClosed && handleInfoWindowClosed(item);
		setSelectedMarkers({});
	};

	// The first idle applies `zoomLevel`; later idles only report the bounds. (Without `center`,
	// the effect below also re-fits and re-zooms whenever `mapData` changes.)
	const handleIdleEvent = () => {
		if (updateBoundaryFilter && map && map.getBounds()) {
			updateBoundaryFilter(
				map.getBounds().getNorthEast().lat(),
				map.getBounds().getSouthWest().lng(),
				map.getBounds().getSouthWest().lat(),
				map.getBounds().getNorthEast().lng()
			);
		}
		if (!hasIdled) {
			map && map.setZoom(zoomLevel);
		}
		setHasIdled(true);
	};

	const isMountedRef = useRef<boolean>();

	const mapStyles = {
		height: height ? `${height}px` : "inherit",
		width: width ? `${width}px` : "inherit",
	};

	const googMapsAvailable =
		typeof (window as any).google === "object" &&
		typeof (window as any).google.maps === "object";

	// `size` is only checked for truthiness below (`{}` is truthy too); no marker icon uses it.
	const [w, h] = customMarkerScale;
	const size =
		(googMapsAvailable &&
			(window as any).google.maps.Size &&
			new (window as any).google.maps.Size(w, h)) ||
		{};

	// Set Boundary of Viewport based on Markers Presented
	useEffect(() => {
		if (mapData && map && !center) {
			try {
				const bounds = new (window as any).google.maps.LatLngBounds();
				mapData.forEach((item) => bounds.extend(item.location));
				map.fitBounds(bounds);

				// Set the zoom level after the map bounds settle
				window.google.maps.event.addListenerOnce(map, "idle", () => {
					map.setZoom(zoomLevel);
				});
			} catch (error) {
				// Drops the map instance, which stops the bounds callbacks and this effect.
				setMap(null);
			}
		}
	}, [mapData, map]);

	useEffect(() => {
		isMountedRef.current = true;
		if (isMountedRef && markerToSelect) {
			setSelectedMarkers(markerToSelect);
		}
		return () => {
			isMountedRef.current = false;
		};
	}, [markerToSelect]);

	// when center changes, locations listed will also change
	// so update it when a new center is passed in
	useEffect(() => {
		if (center) {
			setCenterPosition(center);
		}
	}, [center]);

	useEffect(() => {
		setHasMounted(true);
	}, []);

	return isLoaded && googleKey && hasMounted ? (
		<React.Fragment>
			{size && (
				<GoogleMap
					data-test-id={dataTestId}
					id={id}
					mapContainerClassName={`map-component-root ${className}`}
					mapContainerStyle={mapStyles}
					onDragEnd={handleDragEndEvent}
					center={centerPosition}
					onLoad={onLoad}
					onUnmount={onUnmount}
					onIdle={handleIdleEvent}
				>
					{mapData.length &&
						mapData.map((item: any, i: number) => (
							<React.Fragment key={`${i}-marker`}>
								<Marker
									onMouseOver={({ domEvent: e }: any) => {
										handleMarkerHover && handleMarkerHover(e, item);
									}}
									clickable={handleMarkerSelected ? true : false}
									key={item.name}
									title={`${item.title}` || `${i}-marker`}
									position={item.location}
									icon={
										item.customMarker && googMapsAvailable
											? {
													url: item.customMarker,
													labelOrigin: new window.google.maps.Point(
														16,
														18
													),
											  }
											: customMarker && googMapsAvailable
											? {
													url: customMarker,
													labelOrigin: new window.google.maps.Point(
														16,
														18
													),
											  }
											: undefined
									}
									label={
										item.iconLabel && googMapsAvailable
											? item.iconLabel
											: iconLabel && googMapsAvailable
											? iconLabel
											: undefined
									}
									onClick={(e) => {
										handleMarkerClicked && handleMarkerClicked(e, item);
									}}
								>
									{" "}
								</Marker>

								{!!infoWindowContent && selectedMarkers.id === item.id ? (
									<InfoWindow
										options={{
											pixelOffset: new (window as any).google.maps.Size(
												0,
												-40
											),
										}}
										position={selectedMarkers.location}
										onCloseClick={() => handleCloseInfoWindow(item)}
									>
										{infoWindowContent && infoWindowContent(selectedMarkers)}
									</InfoWindow>
								) : null}
							</React.Fragment>
						))}
				</GoogleMap>
			)}
		</React.Fragment>
	) : null;
};

export default Map;
